/**
 * Sanitization utilities for numeric values
 * 
 * Ensures that NaN/Infinity values never reach the UI or export.
 * Provides consistent formatting for missing or invalid values.
 */

/**
 * Returns the number if finite, otherwise returns null
 */
export function finiteOrNull(n: number | null | undefined): number | null {
  if (n == null) return null;
  if (!Number.isFinite(n)) return null;
  return n;
}

/**
 * Formats a number as GBP currency or returns "—" for null/invalid values
 */
export function gbpOrDash(n: number | null | undefined): string {
  const finite = finiteOrNull(n);
  if (finite === null) return '—';
  return `£${Math.round(finite).toLocaleString()}`;
}

/**
 * Formats a number as percentage or returns "—" for null/invalid values
 */
export function percentOrDash(n: number | null | undefined, decimals: number = 0): string {
  const finite = finiteOrNull(n);
  if (finite === null) return '—';
  return `${(finite * 100).toFixed(decimals)}%`;
}

/**
 * Formats a number with optional unit or returns "—" for null/invalid values
 */
export function numberOrDash(n: number | null | undefined, unit: string = '', decimals: number = 0): string {
  const finite = finiteOrNull(n);
  if (finite === null) return '—';
  return `${finite.toFixed(decimals)}${unit}`;
}

/**
 * Check if a value is missing or invalid and return a human-readable issue
 */
export function checkValue(
  value: number | null | undefined,
  fieldName: string,
  reason: string = 'Not enough information'
): string | null {
  const finite = finiteOrNull(value);
  if (finite === null) {
    return `${fieldName}: ${reason}`;
  }
  return null;
}

/**
 * Sanitize an object's numeric properties recursively
 */
export function sanitizeNumericFields<T extends Record<string, any>>(obj: T): T {
  const result = { ...obj };
  
  for (const key in result) {
    const value = result[key];
    
    if (typeof value === 'number') {
      // Use type assertion after explicit check
      (result as Record<string, any>)[key] = finiteOrNull(value);
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      (result as Record<string, any>)[key] = sanitizeNumericFields(value);
    } else if (Array.isArray(value)) {
      (result as Record<string, any>)[key] = value.map((item: unknown) =>
        (item && typeof item === 'object') ? sanitizeNumericFields(item) : item
      );
    }
  }
  
  return result;
}

/**
 * Normalize optional string fields that may contain "unknown" sentinel
 * 
 * Normalizes user input by:
 * - Trimming whitespace
 * - Converting empty strings to undefined
 * - Converting "unknown" (case-insensitive) to undefined
 * - Preserving all other values
 * 
 * This allows UI to display user-typed text (including "unknown"),
 * while ensuring the engine receives undefined for empty/unknown values.
 * 
 * @param value - The raw user input
 * @returns undefined for empty/unknown, trimmed value otherwise
 */
export function normalizeUnknown(value: string | undefined | null): string | undefined {
  // Handle null/undefined
  if (value == null) return undefined;

  // Trim and check if empty
  const trimmed = value.trim();
  if (trimmed === '') return undefined;

  // Check if it's the "unknown" sentinel (case-insensitive)
  if (trimmed.toLowerCase() === 'unknown') return undefined;

  // Return the original trimmed value
  return trimmed;
}

/**
 * Normalize a free-text field using presence-based logic.
 *
 * Unlike normalizeUnknown (which maps "" → undefined), this function
 * distinguishes "field exists but is empty" from "field was never set":
 *
 *  - null / undefined  →  null   (field not set)
 *  - ""                →  null   (user explicitly cleared the field)
 *  - "unknown"         →  null   (sentinel)
 *  - "  some value  "  →  "some value"  (trimmed)
 *
 * Returning null (rather than undefined) ensures the normalised object
 * always carries the key, so downstream change-detection (e.g.
 * detectDroppedFields) sees the update and suppresses DROPPED FIELD
 * warnings.
 */
export function normalizeTextField(value: string | undefined | null): string | null {
  if (value == null) return null;

  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (trimmed.toLowerCase() === 'unknown') return null;

  return trimmed;
}

/**
 * Safely parse a numeric value that might be a string or number.
 * 
 * Handles common cases:
 * - Numbers: returned as-is if finite
 * - Numeric strings: parsed to numbers
 * - Empty/whitespace strings: returns undefined
 * - "unknown" (case-insensitive): returns undefined
 * - Invalid values: returns undefined
 * 
 * @param value - The value to parse (string, number, undefined, null)
 * @returns A finite number or undefined
 */
export function safeParseNumber(value: string | number | undefined | null): number | undefined {
  // Handle null/undefined
  if (value == null) return undefined;
  
  // If already a number, check if finite
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  
  // Handle string values
  if (typeof value === 'string') {
    const trimmed = value.trim();
    
    // Empty or "unknown" sentinel
    if (trimmed === '' || trimmed.toLowerCase() === 'unknown') {
      return undefined;
    }
    
    // Try to parse as number
    const parsed = Number.parseFloat(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  
  // Unexpected type
  return undefined;
}

/**
 * Safely parse an integer value that might be a string or number.
 * Same as safeParseNumber but returns integers only.
 * 
 * @param value - The value to parse (string, number, undefined, null)
 * @returns A finite integer or undefined
 */
export function safeParseInt(value: string | number | undefined | null): number | undefined {
  const parsed = safeParseNumber(value);
  if (parsed === undefined) return undefined;
  return Math.round(parsed);
}
